import { GridColDef } from '@mui/x-data-grid';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';

export enum ReportType {
  PATIENT = 'PATIENT',
  DIAGNOSIS = 'DIAGNOSIS',
  PRESCRIPTION = 'PRESCRIPTION',
}

const EXCLUDED_COLUMNS = [
  'id',
  'adherence',
  'linac',
  'sim_to_treat',
  'referrer',
  'diagnosis',
  'additional_prescription_info',
  'basic_dose_calculation',
  'careplanstatus',
  'bolus_location_type',
  'Address',
  'fractionDose',
  'microdosimetry_tld',
  'weekly_physics_check',
  'is_3d_bolus',
  'location',
];

const EXCLUDED_PREFIXES = ['cpot', 'user_acknowledged', 'treatment_break', 'custom'];

const COLUMN_ORDER = [
  'originalId',
  'createdAt',
  'submittedAt',
  'careplanTemplateName',
  'patient',
  'patientMrn',
  'ida',
  'firstName',
  'lastName',
  'dateOfBirth',
  'createdBy',
  'isPrimaryDiagnosis',
  'relatedPrimaryDiagnosisId',
  'treatingDepartment',
  'diagnosisId',
  'tumourStream',
  'diagnosisCode',
  'diagnosisDescription',
  'AJCC_DiseaseTitle',
  'AJCC_DiseaseChapter',
  'priority',
  'siteGroupId',
  'treatmentSite',
  'location',
  'laterality',
  'diagnosisDate',
  'morphology',
  'dose',
  'fractions',
  'phase',
  'technique',
  'modality',
  'dose_to',
  'dose_frequency',
  'imaging',
  'imaging_technique',
  'bolus',
  'bolus_location',
  'bolus_thickness',
  'bolus_frequency',
  'bolus_location_type',
  'gtv',
  'ptv',
  'ctv',
];

const cleanValue = (value: any): any => {
  if (typeof value !== 'string') return value;

  const cleaned = value.replace(/["\[\]]/g, '');
  const lower = cleaned.toLowerCase();

  if (lower === 'true') return 'Yes';
  if (lower === 'false') return 'No';

  return cleaned;
};

const cleanRecord = (record: any): any => {
  const cleaned = { ...record };
  Object.keys(cleaned).forEach((key) => {
    cleaned[key] = cleanValue(cleaned[key]);
  });
  return cleaned;
};

export const applyDateFilter = (data: any[], dateRange: [Dayjs | null, Dayjs | null]): any[] => {
  const [startDate, endDate] = dateRange;
  if (!startDate && !endDate) return data;

  return data.filter((record) => {
    const recordDate = dayjs(record.createdAt || record.submittedAt);
    if (startDate && endDate) {
      return (
        (recordDate.isAfter(startDate) || recordDate.isSame(startDate)) &&
        (recordDate.isBefore(endDate) || recordDate.isSame(endDate))
      );
    }
    if (startDate) return recordDate.isAfter(startDate) || recordDate.isSame(startDate);
    if (endDate) return recordDate.isBefore(endDate) || recordDate.isSame(endDate);
    return true;
  });
};

const buildPatientAddress = (address: any): string => {
  if (!address) return '';
  const parts = [address.line1, address.line2, address.city, address.state, address.postcode];
  return parts.filter(Boolean).join(' ');
};

export const processPatientData = (patientData: any): any[] => {
  const patients = patientData?.allPatients || [];

  return patients.map((patient: any, index: number) => {
    const processed = cleanRecord(patient);
    processed.id = patient.ida || `patient_${index}`;
    processed.fullAddress = buildPatientAddress(patient.address);
    if ('dob' in processed) {
      processed.dateOfBirth = processed.dob;
      delete processed.dob;
    }
    return processed;
  });
};

const createDiagnosisRow = (diagnosisData: any, careplan: any, isRelatedPrimary: boolean, index: number) => {
  const row = {
    diagnosisId: diagnosisData.id,
    isPrimaryDiagnosis: diagnosisData.isPrimaryDiagnosis,
    stageType: diagnosisData.stageType,
    laterality: diagnosisData.laterality,
    diagnosisDate: diagnosisData.diagnosisDate,
    morphology: diagnosisData.morphology?.displayName,
    diagnosisCode: diagnosisData.diagnosisCode?.diagnosisCode,
    diagnosisDescription: diagnosisData.diagnosisCode?.diagnosisDescription,
    tumourStream: diagnosisData.diagnosisCode?.tumourStream?.name,
    T_StagePathological: diagnosisData.stageTCodePathological?.subCode,
    N_StagePathological: diagnosisData.stageNCodePathological?.subCode,
    M_StagePathological: diagnosisData.stageMCodePathological?.subCode,
    T_StageClinical: diagnosisData.stageTCodeClinical?.subCode,
    N_StageClinical: diagnosisData.stageNCodeClinical?.subCode,
    M_StageClinical: diagnosisData.stageMCodeClinical?.subCode,
    AJCC_DiseaseTitle: diagnosisData.disease?.title,
    AJCC_DiseaseChapter: diagnosisData.disease?.chapter,
    relatedPrimaryDiagnosisId: !isRelatedPrimary ? careplan.diagnosis.relatedPrimaryDiagnosis?.id : null,
    careplanStatus: careplan.careplanStatus,
    createdAt: careplan.createdAt,
    createdBy: careplan.createdBy,
    patientMRN: careplan.patient?.ida,
    id: isRelatedPrimary ? `${diagnosisData.id}_related_${index}` : diagnosisData.id || `diagnosis_${index}`,
  };

  return cleanRecord(row);
};

export const processDiagnosisData = (diagnosisData: any): any[] => {
  const careplans = diagnosisData?.careplans || [];
  const processedDiagnoses: any[] = [];

  careplans
    .filter((careplan: any) => careplan.diagnosis)
    .forEach((careplan: any, index: number) => {
      const diagnosis = careplan.diagnosis;

      processedDiagnoses.push(createDiagnosisRow(diagnosis, careplan, false, index));

      if (diagnosis.relatedPrimaryDiagnosis) {
        processedDiagnoses.push(createDiagnosisRow(diagnosis.relatedPrimaryDiagnosis, careplan, true, index));
      }
    });

  return processedDiagnoses;
};

export const filterDataByReportType = (
  reportType: ReportType,
  filteredData: any[],
  patientData: any,
  diagnosisData?: any,
): any[] => {
  switch (reportType) {
    case ReportType.PATIENT:
      return processPatientData(patientData);
    case ReportType.DIAGNOSIS:
      return processDiagnosisData(diagnosisData);
    case ReportType.PRESCRIPTION:
      return filteredData
        .filter(
          (record) =>
            record.__typename === 'CarepathCareplanType' && record.careplanStatus?.toLowerCase() === 'prescription',
        )
        .map((record) => {
          const processed = { ...record };
          if (processed.diagnosisCode) {
            const [code, ...descriptionParts] = processed.diagnosisCode.split(' ');
            processed.diagnosisCode = code;
            processed.diagnosisDescription = descriptionParts.join(' ');
          }
          // Rename careplanName to careplanTemplateName
          if ('careplanName' in processed) {
            processed.careplanTemplateName = processed.careplanName;
            delete processed.careplanName;
          }
          return processed;
        });
    default:
      return [];
  }
};

const createSiteRecord = (record: any, siteGroup: any, site: any, siteIndex: number, recordIndex: number) => {
  const expandedRecord = JSON.parse(JSON.stringify(record));

  delete expandedRecord.sitegroupSet;
  delete expandedRecord.technique;

  expandedRecord.originalId = record.id;
  expandedRecord.id = `${record.id || record.uuid || recordIndex}_site_${siteIndex}`;
  expandedRecord.siteGroupId = siteGroup.id;
  expandedRecord.treatmentSite = site.treatmentSite?.treatmentSite;

  // Process site values
  if (Array.isArray(site.sitevaluesSet)) {
    site.sitevaluesSet.forEach((siteValue: any) => {
      if (
        siteValue.field?.name &&
        siteValue.value !== undefined &&
        siteValue.field.name.toLowerCase() !== 'fractions'
      ) {
        expandedRecord[siteValue.field.name] = cleanValue(siteValue.value);
      }
    });
  }

  // Process bolus thickness
  if (expandedRecord.Bolus_Thickness?.toLowerCase() === 'custom' && expandedRecord.Bolus_Thickness_Custom) {
    expandedRecord.Bolus_Thickness = expandedRecord.Bolus_Thickness_Custom;
  }
  delete expandedRecord.Bolus_Thickness_Custom;

  // Process dose frequency
  if (expandedRecord.Dose_Frequency?.toLowerCase() === 'custom' && expandedRecord.Custom_Dose_Frequency) {
    expandedRecord.Dose_Frequency = expandedRecord.Custom_Dose_Frequency;
  }
  delete expandedRecord.Custom_Dose_Frequency;

  // Process treatment site
  if (expandedRecord.treatmentSite?.includes('Specify') && expandedRecord.Location) {
    expandedRecord.treatmentSite = expandedRecord.treatmentSite.replace('Specify', expandedRecord.Location);
  }

  return cleanRecord(expandedRecord);
};

export const expandSiteGroupData = (data: any[]): any[] => {
  const expandedData: any[] = [];

  data.forEach((record, recordIndex) => {
    let siteIndex = 0;
    record.sitegroupSet.forEach((siteGroup: any) => {
      siteGroup.siteSet.forEach((site: any) => {
        const siteRecord = createSiteRecord(record, siteGroup, site, siteIndex, recordIndex);
        expandedData.push(siteRecord);
        siteIndex++;
      });
    });
  });

  return expandedData;
};

const shouldExcludeColumn = (key: string): boolean => {
  const keyLower = key.toLowerCase();

  if (key === '__typename' || EXCLUDED_COLUMNS.some((col) => col.toLowerCase() === keyLower)) {
    return true;
  }

  return EXCLUDED_PREFIXES.some((prefix) => keyLower.startsWith(prefix));
};

const createHeaderName = (key: string): string => {
  if (key === 'originalId') return 'Careplan Id';
  if (key === 'ida') return 'Patient MRN';

  const keyWithoutUnderscores = key.replace(/_/g, '');
  const isAllCaps =
    keyWithoutUnderscores === keyWithoutUnderscores.toUpperCase() &&
    keyWithoutUnderscores !== keyWithoutUnderscores.toLowerCase();

  if (isAllCaps) {
    return key.replace(/_/g, ' ');
  }

  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

const getDateFormat = () => {
  const dateFormat = CurrentAppConfig.Insights?.chartDateFormat || 'DD/MM/YYYY';
  const isUSFormat = dateFormat === 'MM/DD/YYYY';
  return {
    locale: isUSFormat ? 'en-US' : 'en-GB',
    dateOptions: isUSFormat
      ? { month: '2-digit', day: '2-digit', year: 'numeric' }
      : { day: '2-digit', month: '2-digit', year: 'numeric' },
  };
};

const formatDateValue = (fieldName: string, value: any): string => {
  if (!value) return '';

  const date = new Date(value);
  const { locale, dateOptions } = getDateFormat();

  if (fieldName === 'createdAt' || fieldName === 'submittedAt') {
    const dateStr = date.toLocaleDateString(locale, dateOptions as Intl.DateTimeFormatOptions);
    const timeStr = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${dateStr} ${timeStr}`;
  }

  if (fieldName.toLowerCase().endsWith('date') || fieldName === 'dateOfBirth') {
    return date.toLocaleDateString(locale, dateOptions as Intl.DateTimeFormatOptions);
  }

  return value;
};

const formatFieldValue = (field: string, value: any): any => {
  if (isDateField(field)) {
    return formatDateValue(field, value);
  }

  if (field === 'carepathPatientDiagnosis' && value?.[0]?.diagnosisCode) {
    const diagnosis = value[0].diagnosisCode;
    return `${diagnosis.diagnosisCode} - ${diagnosis.diagnosisDescription}`;
  }

  if (field === 'patient' && value) {
    return value.ida || '';
  }

  return value;
};

const isDateField = (key: string): boolean => {
  return key === 'createdAt' || key === 'submittedAt' || key.toLowerCase().endsWith('date') || key === 'dateOfBirth';
};

const isNumericField = (key: string): boolean => {
  const numericFields = ['originalId', 'diagnosisId', 'siteGroupId', 'fractions'];
  return numericFields.includes(key);
};

const createColumnDef = (key: string): GridColDef => {
  const column: GridColDef = {
    field: key,
    headerName: key === 'patient' ? 'Patient MRN' : createHeaderName(key),
    width: 150,
    minWidth: 120,
  };

  if (isNumericField(key)) {
    column.type = 'number';
    column.renderCell = (params: any) => {
      const value = params.value;
      if (!value) return '';
      return Number(value) || '';
    };
  } else if (isDateField(key) || key === 'carepathPatientDiagnosis' || key === 'patient') {
    column.renderCell = (params: any) => {
      const formattedValue = formatFieldValue(key, params.value);
      return key === 'carepathPatientDiagnosis' || key === 'patient' ? formattedValue : <span>{formattedValue}</span>;
    };
  }

  return column;
};

export const generateColumns = (reportData: any[], reportType?: ReportType): GridColDef[] => {
  if (reportData.length === 0) return [];

  const allKeys = new Set<string>();
  reportData.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (!shouldExcludeColumn(key)) {
        allKeys.add(key);
      }
    });
  });

  const columnOrder =
    reportType === ReportType.DIAGNOSIS
      ? ['diagnosisId', ...COLUMN_ORDER.filter((col) => col !== 'diagnosisId')]
      : COLUMN_ORDER;

  const orderedColumns: GridColDef[] = [];
  const remainingKeys = new Set(allKeys);

  columnOrder.forEach((columnKey) => {
    const matchingKey = Array.from(allKeys).find((key) => key.toLowerCase() === columnKey.toLowerCase());
    if (matchingKey) {
      orderedColumns.push(createColumnDef(matchingKey));
      remainingKeys.delete(matchingKey);
    }
  });

  const additionalColumns = Array.from(remainingKeys).map((key) => createColumnDef(key));
  return [...orderedColumns, ...additionalColumns];
};

export const applySorting = (data: any[], sortModel: any): any[] => {
  if (!sortModel) return data;

  const { field, sort } = sortModel;
  return [...data].sort((a, b) => {
    const aVal = a[field];
    const bVal = b[field];

    if (aVal == null) return -1;
    if (bVal == null) return 1;

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sort === 'desc' ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sort === 'desc' ? bVal - aVal : aVal - bVal;
    }

    return sort === 'desc' ? String(bVal).localeCompare(String(aVal)) : String(aVal).localeCompare(String(bVal));
  });
};

const formatCellForCSV = (col: any, cellValue: any): string => {
  if (!cellValue) return '';

  let formatted = formatFieldValue(col.field, cellValue);
  formatted = String(formatted);

  const needsQuoting =
    formatted.includes('"') || formatted.includes(',') || formatted.includes('\n') || formatted.includes('\r');

  if (needsQuoting) {
    return `"${formatted.replace(/"/g, '""')}"`;
  }

  return formatted;
};

export const generateCSVReport = (reportType: ReportType, visibleColumns: any[], reportData: any[]): void => {
  if (reportData.length === 0 || visibleColumns.length === 0) return;

  const headers = visibleColumns.map((col) => col.headerName).join(',');
  const rows = reportData.map((row) =>
    visibleColumns.map((col) => formatCellForCSV(col, row[col.field] || '')).join(','),
  );

  const csvContent = [headers, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  link.href = URL.createObjectURL(blob);
  link.download = `lumonus_${reportType.toLowerCase()}_report_${new Date().toISOString().split('T')[0]}.csv`;
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
